import {
  ArrowLeft, CalendarDays, History as HistoryIcon, MoreHorizontal, Redo2, RotateCcw, Sparkles, Undo2,
} from "lucide-react";
import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Page } from "@/components/layout/page";
import { TourButton, useTour } from "@/components/layout/tour";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { Tooltip } from "@/components/ui/misc";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/components/ui/toast";
import { CadenceBuilder } from "@/features/cadence-builder";
import { CadencePreview, PlanStrip, planWarnings } from "@/features/cadence-preview";
import { TextPicker, Token } from "@/features/cadence-tokens";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  CADENCE_SLUG_RE, bodyFromForm, formFromBody, formProblems, hasTexts, type CadenceFormValues,
} from "@/lib/cadence";
import { TEMPLATES, fromTemplate, planCalls, slugFrom, type CadenceTemplate } from "@/lib/cadence-plan";
import { sameValues } from "@/lib/form";
import { useLeaveGuard } from "@/lib/leave-guard";
import {
  useCadence, useCadenceCampaigns, useCadenceTextFields, useCadenceVersions, useCadences, useCreateCadence,
  useCreateCadenceVersion, useUpdateCadence,
} from "@/lib/queries";
import type { Tour } from "@/lib/tours";
import { atLeast, type Cadence } from "@/lib/types";
import { useHistory } from "@/lib/use-history";
import { cn, formatDateTime } from "@/lib/utils";

/**
 * The page's two product tours (`components/layout/tour.tsx`), one per state. A new cadence opens
 * on the shapes to start from, and the plan is not on the page until one is picked, so the shapes
 * have a tour of their own and the plan's starts after the pick.
 */
const SHAPES_TOUR: Tour = {
  id: "cadence_shapes",
  version: 1,
  steps: [
    {
      target: "cadence-shapes",
      title: "Start from a shape",
      body: "Each card is a ready-made plan. Its strip shows the first three weeks, one cell per day, darker on a day with more calls. Pick the closest: every call, wait and day can be changed next.",
      side: "top",
    },
  ],
};

/** The plan's steps follow the page from top to bottom. The versions and Save are on the page only
 *  when there is something to show: a saved cadence has versions, a changed one has Save. Version 2
 *  is the page that shows each call as one line and opens it on a click (ADR-103). */
const CADENCE_TOUR: Tour = {
  id: "cadence",
  version: 2,
  steps: [
    {
      target: "cadence-summary",
      title: "In short",
      body: "The whole cadence in a few lines: what each phase calls and how often, when it stops, and the daily cap. It follows every change.",
    },
    {
      target: "cadence-start",
      title: "A lead joins the campaign",
      body: "The plan starts here: how long after a lead joins the first call goes out.",
    },
    {
      target: "cadence-phase",
      title: "A phase",
      body: "A phase is a round of calls that starts again until the phase's last day. Its gear holds its name, its priority and what happens to a call that runs late. A later phase folds to one line; the arrow opens it.",
    },
    {
      target: "cadence-call",
      title: "A call",
      body: "Each call is one line: the number it calls from, and a tag for anything that differs from the defaults. Click it for all its options: what to do if a voicemail answers, redials with the same rep, and whether it is made in every round or only once.",
    },
    {
      target: "cadence-add-text",
      title: "A text",
      body: "A step can be a text instead of a call. Add one where a call is added, write its words, and put a lead's own details in from Insert field. It goes out when its step is due, with nobody signed in, and it spends no call.",
      minimum: "admin",
    },
    {
      target: "cadence-stop",
      title: "The cadence stops",
      body: "The limits: how many calls a lead gets in all, the last day, and how many calls one number gets in a day.",
    },
    {
      target: "cadence-rules",
      title: "More rules",
      body: "What happens around the plan: a call that fails on our side, a hang-up while connecting, a callback with no time, what counts as an attempt, and who is called first when reps are short.",
      side: "top",
    },
    {
      target: "cadence-preview",
      title: "What a lead gets",
      body: "Opens the plan as a calendar, for a lead who never picks up: every call on the day it goes out. It is worked out again on every change.",
      side: "bottom",
      align: "end",
    },
    {
      target: "cadence-history",
      title: "Undo and redo",
      body: "Every edit can be undone until it is saved.",
      minimum: "admin",
      align: "end",
    },
    {
      target: "cadence-versions",
      title: "Versions",
      body: "Every save is a new version, kept here with its note. A lead on the cadence reads the newest version at its next move, and nothing already scheduled is rewritten.",
      align: "end",
    },
    {
      target: "cadence-save",
      title: "Save",
      body: "Nothing changes for the dialer until the plan is saved. A note beside the button can say what changed.",
      minimum: "admin",
      side: "top",
      align: "end",
    },
  ],
};

/**
 * One cadence, as a page (ADR-066/067): the plan a lead goes down on the left, what it adds up to
 * on the right, recomputed as it changes. A new cadence starts from a template. Every edit can be
 * undone (⌘Z) until it is saved, and saving an existing cadence makes a new version: every lead on
 * it reads the new version at its next move, and nothing already scheduled is rewritten.
 *
 * Supervisors read it; admins change it.
 */
export function CadencePage({ creating = false }: { creating?: boolean }) {
  const params = useParams();
  const id = creating ? undefined : Number(params.cadenceId);
  const { user } = useAuth();
  const canManage = atLeast(user?.role, "admin");
  const cadence = useCadence(Number.isFinite(id) ? id : undefined);

  if (creating) {
    return canManage ? <Editor key="new" cadence={null} canManage /> : <NotAllowed />;
  }
  if (cadence.isPending) {
    return (
      <Page>
        <div className="grid place-items-center py-24">
          <Spinner />
        </div>
      </Page>
    );
  }
  if (cadence.error || !cadence.data) {
    return (
      <Page>
        <ErrorState
          error={cadence.error}
          title="This cadence could not be loaded"
          action={
            <Button variant="outline" asChild>
              <Link to="/cadences">Back to cadences</Link>
            </Button>
          }
        />
      </Page>
    );
  }
  return <Editor key={cadence.data.id} cadence={cadence.data} canManage={canManage} />;
}

function NotAllowed() {
  return (
    <Page>
      <EmptyState title="Admins create cadences" description="A supervisor can read every cadence and see what it does." />
    </Page>
  );
}

const valuesOf = (c: Cadence): CadenceFormValues =>
  c.current_version ? formFromBody(c.current_version.body, c.name, c.slug) : { ...fromTemplate(TEMPLATES[0]!), name: c.name, slug: c.slug };

/** What decides "changed": everything but the version note. */
const comparable = (v: CadenceFormValues) => ({ ...v, note: "" });

function Editor({ cadence, canManage }: { cadence: Cadence | null; canManage: boolean }) {
  const creating = cadence === null;
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const catalog = useCadences({ enabled: creating });
  const campaigns = useCadenceCampaigns(cadence?.id);
  const versions = useCadenceVersions(cadence?.id);
  const create = useCreateCadence();
  const version = useCreateCadenceVersion();
  const update = useUpdateCadence();

  const [baseline, setBaseline] = React.useState<CadenceFormValues | null>(() => (cadence ? valuesOf(cadence) : null));
  const history = useHistory<CadenceFormValues>(baseline ?? fromTemplate(TEMPLATES[0]!));
  const values = history.value;
  const [picked, setPicked] = React.useState<CadenceTemplate | null>(null);
  const [slugTouched, setSlugTouched] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [retiring, setRetiring] = React.useState(false);

  const readOnly = !canManage;
  const taken = React.useMemo(() => (catalog.data ?? []).map((c) => c.slug), [catalog.data]);
  const choosing = creating && picked === null;
  // Closed until someone opens it, and then it takes no room: the plan gets the page (ACL-628).
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const warnings = React.useMemo(() => (choosing ? 0 : planWarnings(values)), [choosing, values]);
  const closePreview = () => {
    setPreviewOpen(false);
    // The card's own close button goes with it; the one that opens it again takes the focus.
    document.getElementById("cadence-preview-toggle")?.focus();
  };
  const dirty = creating ? !choosing : !sameValues(comparable(values), comparable(baseline ?? values));
  // Leaving with unsaved changes asks first, however the page is left (ACL-404).
  const { dialog: leaveDialog, release: releaseLeave } = useLeaveGuard(
    dirty && !readOnly,
    "The changes to this cadence are not saved. Leaving drops them.",
  );
  // The fields a text may use, an admin's to read, asked for once the cadence has a text. Until
  // they are in, a text's field names are not judged.
  const textFields = useCadenceTextFields(!readOnly && hasTexts(values)).data;
  const knownFields = React.useMemo(() => (textFields ? new Set(textFields.map((f) => f.name)) : undefined), [textFields]);
  const allProblems = formProblems(values, knownFields);
  const problems = Object.fromEntries(
    Object.entries(allProblems).filter(([key]) => submitted || (key !== "name" && key !== "slug")),
  );
  const count = Object.keys(allProblems).length;
  const busy = create.isPending || version.isPending || update.isPending;
  const inUse = campaigns.data ?? [];
  const nextVersion = (cadence?.current_version?.version_n ?? 0) + 1;

  // The versions button is on the page once the versions have loaded, and its step with it.
  useTour(choosing ? SHAPES_TOUR : CADENCE_TOUR, { ready: creating || !versions.isPending });

  // A version another admin saves while this page is open: said, never loaded over the edits.
  const remote = cadence ? valuesOf(cadence) : null;
  const staleBy = remote && baseline && !sameValues(comparable(remote), comparable(baseline)) ? remote : null;
  React.useEffect(() => {
    if (staleBy && !dirty) {
      setBaseline(staleBy);
      history.reset(staleBy);
    }
  }, [staleBy, dirty, history]);

  const pick = (t: CadenceTemplate) => {
    const v = fromTemplate(t);
    const next = { ...v, name: v.name, slug: slugFrom(v.name, taken) };
    setPicked(t);
    setSlugTouched(false);
    history.reset(next);
  };

  const setName = (name: string) =>
    history.set((v) => ({ ...v, name, ...(creating && !slugTouched ? { slug: slugFrom(name, taken) } : {}) }), "name");

  const save = React.useCallback(async () => {
    if (readOnly || busy || choosing) return;
    setSubmitted(true);
    setSaveError(null);
    if (Object.keys(formProblems(values, knownFields)).length > 0) return;
    const body = bodyFromForm(values, cadence?.current_version?.body ?? null);
    const note = values.note.trim() || null;
    try {
      if (creating) {
        const made = await create.mutateAsync({
          slug: values.slug,
          name: values.name.trim(),
          body,
          note,
          ...(user?.tenant_id ? { tenant_id: user.tenant_id } : {}),
        });
        releaseLeave();
        toast.success("Cadence created", `${values.name.trim()} is ready to attach on a campaign's Dialing tab.`);
        navigate(`/cadences/${made.id}`, { replace: true });
        return;
      }
      const nameChanged = values.name.trim() !== cadence.name;
      const bodyChanged = !sameValues(
        comparable({ ...values, name: "" }),
        comparable({ ...(baseline ?? values), name: "" }),
      );
      if (nameChanged) await update.mutateAsync({ id: cadence.id, name: values.name.trim() });
      if (bodyChanged) {
        const saved = await version.mutateAsync({ id: cadence.id, body, note });
        toast.success(
          `Version ${saved.current_version?.version_n ?? nextVersion} saved`,
          inUse.length
            ? `${inUse.length === 1 ? "1 campaign reads" : `${inUse.length} campaigns read`} it at each lead's next move.`
            : "No campaign runs it yet.",
        );
      } else if (nameChanged) {
        toast.success("Name saved", values.name.trim());
      }
      const kept = { ...values, note: "" };
      setBaseline(kept);
      history.reset(kept);
      setSubmitted(false);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail = (err as ApiError & { body?: { detail?: unknown } }).body?.detail;
        if (detail && typeof detail === "object" && Array.isArray((detail as { errors?: unknown }).errors)) {
          setSaveError(`The server refused it: ${(detail as { errors: string[] }).errors.join("; ")}`);
        } else {
          setSaveError(err.message);
        }
      } else {
        setSaveError("Could not save the cadence. Please try again.");
      }
    }
  }, [readOnly, busy, choosing, values, knownFields, cadence, creating, create, user, toast, navigate, update, baseline, version, nextVersion, inUse.length, history, releaseLeave]);

  // ⌘Z / ⌘⇧Z to undo and redo outside a text field; ⌘S to save anywhere.
  React.useEffect(() => {
    if (readOnly) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const key = e.key.toLowerCase();
      if (key === "s") {
        e.preventDefault();
        void save();
        return;
      }
      const typing = e.target instanceof HTMLElement && (e.target.closest("input, textarea, [contenteditable=true]") !== null);
      if (typing) return;
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        history.undo();
      } else if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        history.redo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [readOnly, save, history]);

  const retire = (active: boolean) =>
    cadence &&
    update.mutate(
      { id: cadence.id, active },
      {
        onSuccess: () => {
          setRetiring(false);
          toast.success(active ? "Cadence restored" : "Cadence retired", cadence.name);
        },
        onError: (e) => toast.error("Could not change the cadence", e instanceof ApiError ? e.message : "Please try again."),
      },
    );

  return (
    <Page className="pb-0">
      <Link to="/cadences" className="mb-3 inline-flex items-center gap-1 text-xs text-text-muted hover:text-text">
        <ArrowLeft className="size-3.5" aria-hidden />
        Cadences
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 pb-5">
        <div className="min-w-[16rem] flex-1 space-y-1.5">
          {choosing ? (
            <h1 className="text-xl font-semibold tracking-tight text-text">New cadence</h1>
          ) : readOnly ? (
            <h1 className="text-xl font-semibold tracking-tight text-text">{values.name}</h1>
          ) : (
            <input
              aria-label="Cadence name"
              placeholder="Name this cadence"
              value={values.name}
              onChange={(e) => setName(e.target.value)}
              className={cn(
                "-ml-1.5 w-full max-w-xl rounded-md border border-transparent bg-transparent px-1.5 py-0.5 text-xl font-semibold tracking-tight text-text",
                "placeholder:text-text-subtle hover:border-border focus-visible:border-accent-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/25",
                problems.name && "border-danger-9",
              )}
            />
          )}
          {problems.name && <p className="text-xs text-danger-11">{problems.name}</p>}
          {!choosing && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-muted">
            {creating && !readOnly ? (
              <Token
                label="Slug"
                text={values.slug}
                tone="muted"
                invalid={problems.slug}
                title="Slug"
                hint="How attempts and decisions name the cadence. Fixed once it is created."
                content={(close) => (
                  <TextPicker
                    label="Slug"
                    mono
                    value={values.slug}
                    validate={(s) =>
                      !CADENCE_SLUG_RE.test(s.trim())
                        ? "Lowercase letters, digits and underscores, 2 to 80, starting with a letter"
                        : taken.includes(s.trim())
                          ? "Another cadence has this slug"
                          : null
                    }
                    onChange={(slug) => {
                      setSlugTouched(true);
                      history.set((v) => ({ ...v, slug }));
                    }}
                    close={close}
                  />
                )}
              >
                <span className="font-mono">{values.slug || "slug"}</span>
              </Token>
            ) : (
              <span className="font-mono">{values.slug}</span>
            )}
            {cadence?.current_version && <Badge size="sm">v{cadence.current_version.version_n}</Badge>}
            {cadence && !cadence.active && <Badge tone="neutral" size="sm">Retired</Badge>}
            {cadence && (
              <span>
                {inUse.length === 0 ? (
                  "No campaign runs it"
                ) : (
                  <>
                    Run by{" "}
                    {inUse.slice(0, 3).map((c, i) => (
                      <React.Fragment key={c.id}>
                        {i > 0 && ", "}
                        <Link to={`/campaigns/${c.id}?tab=cadence`} className="text-text hover:underline">
                          {c.name}
                        </Link>
                      </React.Fragment>
                    ))}
                    {inUse.length > 3 && ` and ${inUse.length - 3} more`}
                  </>
                )}
              </span>
            )}
            {picked && (
              <span className="inline-flex items-center gap-1">
                <Sparkles className="size-3" aria-hidden /> From “{picked.name}”
              </span>
            )}
          </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {/* The page has a header of its own, so it draws the tour's button itself. */}
          <TourButton />
          {!choosing && (
            <Button
              id="cadence-preview-toggle"
              variant={previewOpen ? "subtle" : "ghost"}
              size="sm"
              aria-expanded={previewOpen}
              onClick={() => (previewOpen ? closePreview() : setPreviewOpen(true))}
              data-tour="cadence-preview"
            >
              <CalendarDays />
              What a lead gets
              {warnings > 0 && (
                <Badge tone="warning" size="sm" title={`${warnings} warning${warnings === 1 ? "" : "s"}`}>
                  {warnings}
                </Badge>
              )}
            </Button>
          )}
          {!readOnly && !choosing && (
            <span className="flex items-center gap-1" data-tour="cadence-history">
              <Tooltip content="Undo (⌘Z)">
                <Button variant="ghost" size="icon-sm" aria-label="Undo" disabled={!history.canUndo} onClick={history.undo}>
                  <Undo2 />
                </Button>
              </Tooltip>
              <Tooltip content="Redo (⌘⇧Z)">
                <Button variant="ghost" size="icon-sm" aria-label="Redo" disabled={!history.canRedo} onClick={history.redo}>
                  <Redo2 />
                </Button>
              </Tooltip>
            </span>
          )}
          {cadence && (versions.data?.length ?? 0) > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" data-tour="cadence-versions">
                  <HistoryIcon />
                  {versions.data!.length} version{versions.data!.length === 1 ? "" : "s"}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-2">
                <ol className="max-h-80 space-y-1 overflow-y-auto">
                  {[...(versions.data ?? [])]
                    .sort((a, b) => b.version_n - a.version_n)
                    .map((v) => (
                      <li key={v.id} className="rounded-md px-2 py-1.5 hover:bg-surface-hover">
                        <div className="flex items-center justify-between gap-2 text-xs">
                          <span className="font-medium text-text">Version {v.version_n}</span>
                          <span className="text-text-subtle">{formatDateTime(v.created_at)}</span>
                        </div>
                        {v.note && <p className="text-xs text-text-muted">{v.note}</p>}
                        <PlanStrip body={v.body} className="mt-1" />
                      </li>
                    ))}
                </ol>
              </PopoverContent>
            </Popover>
          )}
          {!readOnly && (cadence || picked) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label="More">
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {creating && (
                  <DropdownMenuItem onSelect={() => setPicked(null)}>
                    <Sparkles />
                    Start from another template
                  </DropdownMenuItem>
                )}
                {cadence &&
                  (cadence.active ? (
                    <DropdownMenuItem destructive onSelect={() => setRetiring(true)}>
                      Retire
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onSelect={() => retire(true)}>
                      <RotateCcw />
                      Restore
                    </DropdownMenuItem>
                  ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </header>

      {staleBy && dirty && (
        <Callout tone="warning" className="mb-4">
          Someone saved a new version of this cadence while you were editing. Saving now makes yours the newest; discard to
          see theirs.
        </Callout>
      )}
      {(cadence?.unusable_text_fields?.length ?? 0) > 0 && (
        // A field of the tenant's own that was unticked or archived since the cadence was saved
        // (ADR-135 in platform): core-api says which.
        <Callout tone="warning" className="mb-4">
          {cadence!.unusable_text_fields!.length === 1 ? "A field" : "Fields"} this cadence&apos;s texts use can no longer
          be put in a text:{" "}
          <span className="font-mono">{cadence!.unusable_text_fields!.join(", ")}</span>. A text says its fallback words
          there, and one with no fallback words is not sent. Take the field out of the text, or tick it again on the
          Custom fields page.
        </Callout>
      )}

      {choosing ? (
        <TemplateGallery onPick={pick} />
      ) : (
        <div className="@container">
          {/* Opened, side by side when the page has room for both; otherwise the figures sit above the
              plan in a compact form, so a change still shows what it does without scrolling away. */}
          <div className={cn("grid grid-cols-1 items-start gap-6", previewOpen && "@5xl:grid-cols-[minmax(0,1fr)_380px] @5xl:gap-8")}>
            {previewOpen && (
              <div className="@5xl:hidden">
                <CadencePreview values={values} compact onClose={closePreview} />
              </div>
            )}
            <CadenceBuilder
              values={values}
              set={history.set}
              readOnly={readOnly}
              problems={problems}
              // A new cadence is the signed-in admin's tenant's; a saved one is its own tenant's, and the
              // outcome it names is checked against that tenant's catalog, not the reader's.
              tenantId={cadence ? (cadence.tenant_id ?? null) : (user?.tenant_id ?? null)}
            />
            {previewOpen && (
              <aside className="hidden @5xl:sticky @5xl:top-6 @5xl:block">
                <CadencePreview values={values} onClose={closePreview} />
              </aside>
            )}
          </div>
        </div>
      )}

      {!readOnly && !choosing && (dirty || creating) ? (
        <div className="sticky bottom-0 z-20 -mx-6 mt-8 border-t border-border bg-surface/95 px-6 py-3 backdrop-blur">
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1 text-xs">
              {saveError ? (
                <span className="text-danger-11">{saveError}</span>
              ) : submitted && count > 0 ? (
                <span className="text-danger-11">
                  {count === 1 ? "One thing" : `${count} things`} to fix first: {Object.values(allProblems)[0]}
                </span>
              ) : creating ? (
                <span className="text-text-muted">Not saved yet. Nothing dials on it until a campaign picks it.</span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-text-muted">
                  <span className="size-1.5 rounded-full bg-warning-9" aria-hidden />
                  Unsaved changes.{" "}
                  {inUse.length
                    ? `${inUse.length} campaign${inUse.length === 1 ? "" : "s"} will read version ${nextVersion} at each lead's next move.`
                    : "No campaign runs it yet."}
                </span>
              )}
            </div>
            <input
              aria-label={creating ? "Note" : "What changed"}
              placeholder={creating ? "Note (optional)" : "What changed? (optional)"}
              value={values.note}
              onChange={(e) => history.set((v) => ({ ...v, note: e.target.value }), "note")}
              className="h-8 w-56 rounded-md border border-border bg-surface px-2.5 text-sm text-text placeholder:text-text-subtle focus-visible:border-accent-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/25"
            />
            {!creating && (
              <Button
                variant="ghost"
                onClick={() => {
                  if (baseline) history.reset(baseline);
                  setSubmitted(false);
                  setSaveError(null);
                }}
              >
                Discard
              </Button>
            )}
            <Button variant="default" loading={busy} onClick={() => void save()} data-tour="cadence-save">
              {creating ? "Create cadence" : `Save as version ${nextVersion}`}
            </Button>
          </div>
        </div>
      ) : (
        <div className="h-10" />
      )}

      {leaveDialog}
      <ConfirmDialog
        open={retiring}
        onOpenChange={setRetiring}
        title="Retire this cadence?"
        description={`${cadence?.name ?? "It"} keeps running on the campaigns already on it${inUse.length ? ` (${inUse.length})` : ""}, and no new campaign can pick it. It can be restored.`}
        confirmLabel="Retire"
        loading={update.isPending}
        onConfirm={() => retire(false)}
      />
    </Page>
  );
}

/** Where a new cadence starts: the shapes the floor talks about, each with what it adds up to. */
function TemplateGallery({ onPick }: { onPick: (t: CadenceTemplate) => void }) {
  return (
    <section aria-label="Start from" className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-text">Start from a shape</h2>
        <p className="text-sm text-text-muted">Pick the closest. Every call, wait and day can be changed next.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5" data-tour="cadence-shapes">
        {TEMPLATES.map((t) => {
          const plan = planCalls(t.values);
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onPick(t)}
              className={cn(
                "group flex flex-col gap-3 rounded-xl border border-border bg-surface p-4 text-left shadow-xs transition",
                "hover:-translate-y-0.5 hover:border-accent-7 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40",
              )}
            >
              <span className="space-y-1">
                <span className="block text-sm font-semibold text-text">{t.name}</span>
                <span className="block text-xs text-text-muted">{t.description}</span>
              </span>
              <PlanStrip body={bodyFromForm(t.values)} />
              <span className="mt-auto text-xs font-medium text-accent-11 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                Use this · {plan.calls.length} calls{plan.texts.length > 0 ? ` · ${plan.texts.length} text${plan.texts.length === 1 ? "" : "s"}` : ""}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
